import { ResponseModelInterfaceStatic } from "@/service/models/response-model-interface";
import { StdAprioriValue } from "@/service/models/response/drug-common/std-apriori-value";
import { GraphTargetType } from "@/utilities/graphing/lib/graphdata";
import { staticImplements } from "@/utilities/static-implements";
import { XmlElementWrapper } from "@/utilities/xml-helpers/xml-element-wrapper";
import { TargetMic } from "./target-mic";
import { TargetTimes } from "./target-times";

@staticImplements<ResponseModelInterfaceStatic>()
export class Target
{
    public constructor(
        public type: Target.Type,
        public unit: string,
        public min: StdAprioriValue,
        public max: StdAprioriValue,
        public best: StdAprioriValue,
        public toxicityAlarm: StdAprioriValue,
        public inefficacyAlarm: StdAprioriValue,
        public mic: TargetMic|null,
        public times: TargetTimes|null,
        public comments: string[],
    )
    {
    }

    public static makeFromXml(elem: XmlElementWrapper): Target
    {
        const valuesElem = elem.firstChildByTag("targetValues");
        const micElem = valuesElem.firstChildByTagOrNull("mic");
        const timesElem = valuesElem.firstChildByTagOrNull("times");

        return new this(
            elem.firstChildByTag("targetType").enumValue(Target.Type),
            valuesElem.firstChildByTag("unit").value(),
            StdAprioriValue.makeFromXml(valuesElem.firstChildByTag("min")),
            StdAprioriValue.makeFromXml(valuesElem.firstChildByTag("max")),
            StdAprioriValue.makeFromXml(valuesElem.firstChildByTag("best")),
            StdAprioriValue.makeFromXml(valuesElem.firstChildByTag("toxicityAlarm")),
            StdAprioriValue.makeFromXml(valuesElem.firstChildByTag("inefficacyAlarm")),
            (micElem === null) ? null : TargetMic.makeFromXml(micElem),
            (timesElem === null) ? null : TargetTimes.makeFromXml(timesElem),
            elem.firstChildByTag("comments").translatedElemValues("comment"),
        );
    }
}

export namespace Target
{
    export enum Type {
        RESIDUAL = "residual",
        PEAK = "peak",
        MEAN = "mean",
        AUC = "auc",
        AUC_24 = "auc24",
        CUMULATIVE_AUC = "cumulativeAuc",
        AUC_DIVIDED_BY_MIC = "aucDividedByMic",
        AUC_24_DIVIDED_BY_MIC = "auc24DividedByMic",
        AUC_OVER_MIC = "aucOverMic",
        AUC_24_OVER_MIC = "auc24OverMic",
        PEAK_DIVIDED_BY_MIC = "peakDividedByMic",
        TIME_OVER_MIC = "timeOverMic",
        RESIDUAL_DIVIDED_BY_MIC = "residualDividedByMic",
    }

    export const TypeToGraphTargetTypeMapping = new Map<Target.Type, GraphTargetType>([
        [Target.Type.RESIDUAL,                  GraphTargetType.ResidualTarget],
        [Target.Type.PEAK,                      GraphTargetType.PeakTarget],
        [Target.Type.MEAN,                      GraphTargetType.MeanTarget],
        [Target.Type.AUC,                       GraphTargetType.AUCTarget],
        [Target.Type.AUC_24,                    GraphTargetType.AUC24Target],
        [Target.Type.CUMULATIVE_AUC,            GraphTargetType.CumulativeAUCTarget],
        [Target.Type.AUC_DIVIDED_BY_MIC,        GraphTargetType.AUCDividedByMicTarget],
        [Target.Type.AUC_24_DIVIDED_BY_MIC,     GraphTargetType.AUC24DividedByMicTarget],
        [Target.Type.AUC_OVER_MIC,              GraphTargetType.AUCOverMicTarget],
        [Target.Type.AUC_24_OVER_MIC,           GraphTargetType.AUC24OverMicTarget],
        [Target.Type.PEAK_DIVIDED_BY_MIC,       GraphTargetType.PeakDividedByMicTarget],
        [Target.Type.TIME_OVER_MIC,             GraphTargetType.TimeOverMicTarget],
        [Target.Type.RESIDUAL_DIVIDED_BY_MIC,   GraphTargetType.ResidualDividedByMicTarget],
    ]);

    export const TypeToUnitMapping = new Map<Target.Type, string>([
        [Target.Type.RESIDUAL,                  "ug/l"],
        [Target.Type.PEAK,                      "mg/l"],
        [Target.Type.MEAN,                      "ug/l"],
        [Target.Type.AUC,                       "ug*h/l"],
        [Target.Type.AUC_24,                    "ug*h/l"],
        [Target.Type.CUMULATIVE_AUC,            "ug*h/l"],
        [Target.Type.AUC_DIVIDED_BY_MIC,        "h"],
        [Target.Type.AUC_24_DIVIDED_BY_MIC,     "h"],
        [Target.Type.AUC_OVER_MIC,              "ug*h/l"],
        [Target.Type.AUC_24_OVER_MIC,           "ug*h/l"],
        [Target.Type.PEAK_DIVIDED_BY_MIC,       ""],
        [Target.Type.TIME_OVER_MIC,             "h"],
        [Target.Type.RESIDUAL_DIVIDED_BY_MIC,   ""],
    ]);
}
